import { fetchKpopTags } from "./kpop-tags";
import { fetchScopeProductTags, type ScopeProductTagsParams } from "./storefront";
import type { PocaConfig } from "../types";

/**
 * "종류(TYPE)" 태그 — 앨범 포카 · MD · POB · LD · 이벤트 … 처럼 상품의 성격을 나타내는 태그.
 *
 * Shopify 상품 tags 에는 그룹명·멤버명·종류 태그가 섞여 있다. 그룹/멤버는 위의 컬렉션 줄에서
 * 고르므로, 여기서는 kpop-tags 사전(= 종류 사전)에 있는 태그만 종류로 취급한다.
 * 건수는 현재 범위(그룹 · 멤버 · 재고) 안 상품의 tags 만 받아서 브라우저에서 센다.
 *
 * 전체(ALL) 범위는 재고 있는 상품만 5,000개가 넘어 250개씩 20여 페이지 = 10초 가까이 걸린다.
 * 그래서 페이지가 올 때마다 중간 집계를 흘려 보내고(progressive), 끝난 결과는
 * sessionStorage 에 넣어 뒤로가기·새로고침 때 바로 쓴다.
 */

export type TypeTagCount = {
  /** 상품에 붙은 원래 표기 (필터 쿼리에 그대로 쓴다) */
  tag: string;
  count: number;
};

export type TypeTagScope = {
  collectionId: string | null;
  memberTag: string | null;
  availableOnly: boolean;
};

export type TypeTagResult = {
  counts: TypeTagCount[];
  /** 범위 안 상품 수 (All 칩 숫자) */
  total: number;
  /** false 면 아직 페이지를 더 받는 중 (숫자가 늘어날 수 있음) */
  complete: boolean;
};

const DICT_PAGE_SIZE = 200;
const DICT_MAX_PAGES = 20;

let dictionaryPromise: Promise<Set<string>> | null = null;

/** kpop-tags 사전 전체를 소문자 Set 으로. 한 번만 받고 공유한다. */
export function loadTypeTagDictionary(): Promise<Set<string>> {
  if (!dictionaryPromise) {
    dictionaryPromise = (async () => {
      const names = new Set<string>();
      for (let page = 0; page < DICT_MAX_PAGES; page++) {
        const res = await fetchKpopTags(undefined, page, DICT_PAGE_SIZE);
        for (const t of res.content) names.add(normalizeTag(t.name));
        if (res.last || res.content.length === 0) break;
      }
      return names;
    })().catch((e) => {
      dictionaryPromise = null; // 실패하면 다음에 다시 시도
      throw e;
    });
  }
  return dictionaryPromise;
}

export function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

/* ---- scope 별 상품 tags 캐시 (멤버 필터는 캐시 뒤에서 적용) ---- */

type ScopeNodes = Array<{ tags: string[] }>;
type ScopeListener = (nodes: ScopeNodes, complete: boolean) => void;
type ScopeEntry = {
  at: number;
  nodes: ScopeNodes;
  complete: boolean;
  error: unknown;
  listeners: Set<ScopeListener>;
  promise: Promise<ScopeNodes>;
};

const SCOPE_TTL_MS = 10 * 60 * 1000;
const STORAGE_PREFIX = "poca.typeTags.";
const scopeCache = new Map<string, ScopeEntry>();

function scopeKey(p: ScopeProductTagsParams): string {
  return `${p.collectionId ?? "ALL"}|${p.availableOnly ? 1 : 0}`;
}

function readStoredScope(key: string): ScopeNodes | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; tags: string[][] };
    if (Date.now() - parsed.at > SCOPE_TTL_MS) return null;
    return parsed.tags.map((tags) => ({ tags }));
  } catch {
    return null;
  }
}

function writeStoredScope(key: string, nodes: ScopeNodes): void {
  try {
    window.sessionStorage.setItem(
      STORAGE_PREFIX + key,
      JSON.stringify({ at: Date.now(), tags: nodes.map((n) => n.tags) })
    );
  } catch {
    // 용량 초과·프라이빗 모드 등 — 캐시는 없어도 동작엔 지장 없음
  }
}

function getScopeEntry(config: PocaConfig, params: ScopeProductTagsParams): ScopeEntry {
  const key = scopeKey(params);
  const hit = scopeCache.get(key);
  if (hit && !hit.error && Date.now() - hit.at < SCOPE_TTL_MS) return hit;

  const stored = readStoredScope(key);
  if (stored) {
    const entry: ScopeEntry = {
      at: Date.now(),
      nodes: stored,
      complete: true,
      error: null,
      listeners: new Set(),
      promise: Promise.resolve(stored),
    };
    scopeCache.set(key, entry);
    return entry;
  }

  const entry: ScopeEntry = {
    at: Date.now(),
    nodes: [],
    complete: false,
    error: null,
    listeners: new Set(),
    promise: Promise.resolve([]),
  };
  // 캐시용 요청은 화면이 바뀌어도 끊지 않는다 (다음 화면이 그대로 이어받음)
  entry.promise = fetchScopeProductTags(config, params, undefined, (pageNodes) => {
    entry.nodes = entry.nodes.concat(pageNodes);
    for (const l of entry.listeners) l(entry.nodes, false);
  })
    .then((nodes) => {
      entry.nodes = nodes;
      entry.complete = true;
      writeStoredScope(key, nodes);
      for (const l of entry.listeners) l(nodes, true);
      return nodes;
    })
    .catch((e) => {
      entry.error = e;
      scopeCache.delete(key);
      throw e;
    });
  scopeCache.set(key, entry);
  return entry;
}

export function countTypeTags(
  nodes: ScopeNodes,
  dictionary: Set<string>
): TypeTagCount[] {
  const counts = new Map<string, TypeTagCount>();
  for (const n of nodes) {
    const seen = new Set<string>();
    for (const raw of n.tags) {
      const key = normalizeTag(raw);
      if (!key || seen.has(key) || !dictionary.has(key)) continue;
      seen.add(key);
      const cur = counts.get(key);
      if (cur) cur.count += 1;
      else counts.set(key, { tag: raw.trim(), count: 1 });
    }
  }
  return [...counts.values()].sort(
    (a, b) => b.count - a.count || a.tag.localeCompare(b.tag)
  );
}

function summarize(
  nodes: ScopeNodes,
  dictionary: Set<string>,
  memberTag: string | null,
  complete: boolean
): TypeTagResult {
  const member = memberTag ? normalizeTag(memberTag) : "";
  const inScope = member
    ? nodes.filter((n) => n.tags.some((t) => normalizeTag(t) === member))
    : nodes;
  return { counts: countTypeTags(inScope, dictionary), total: inScope.length, complete };
}

/**
 * 범위의 종류 태그 건수. 페이지가 올 때마다 onUpdate(중간 집계) 를 부르고,
 * 다 받으면 complete=true 인 최종 결과로 resolve 한다. signal 로 구독만 끊는다 (요청은 계속 진행·캐시).
 */
export async function fetchTypeTagCounts(
  config: PocaConfig,
  scope: TypeTagScope,
  signal?: AbortSignal,
  onUpdate?: (partial: TypeTagResult) => void
): Promise<TypeTagResult> {
  const dictionary = await loadTypeTagDictionary();
  if (signal?.aborted) throw new DOMException("aborted", "AbortError");

  const entry = getScopeEntry(config, {
    collectionId: scope.collectionId,
    availableOnly: scope.availableOnly,
  });

  if (entry.complete) {
    return summarize(entry.nodes, dictionary, scope.memberTag, true);
  }

  return new Promise<TypeTagResult>((resolve, reject) => {
    const listener: ScopeListener = (nodes, complete) => {
      if (signal?.aborted) return;
      const result = summarize(nodes, dictionary, scope.memberTag, complete);
      if (complete) {
        cleanup();
        resolve(result);
      } else {
        onUpdate?.(result);
      }
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException("aborted", "AbortError"));
    };
    const cleanup = () => {
      entry.listeners.delete(listener);
      signal?.removeEventListener("abort", onAbort);
    };

    entry.listeners.add(listener);
    signal?.addEventListener("abort", onAbort, { once: true });
    entry.promise.catch((e) => {
      cleanup();
      reject(e);
    });

    // 이미 받아둔 페이지가 있으면 바로 한 번 보여준다
    if (entry.nodes.length > 0) {
      onUpdate?.(summarize(entry.nodes, dictionary, scope.memberTag, false));
    }
  });
}

/** 칩에 보여줄 표기: 첫 글자만 대문자로 (album photocard → Album photocard, MD 는 그대로) */
export function typeTagLabel(tag: string): string {
  const t = tag.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}
